import * as p from '@clack/prompts'
import fs from 'node:fs'
import path from 'node:path'
import type { LoadedAuthoredGeneratedSchema } from '@cli-types/core-engine/add-field.js'
import type { CreateCommandOptions, CreateSchemaKind } from '@cli-types/core-engine/commands.js'
import type { FormSchema } from '@cli-types/core-engine/schema.js'
import { CREATE_PROMPT_CONTEXT } from '@cli-constants/prompt-copy.js'
import { UNSUPPORTED_PROJECT_STATE_MESSAGE } from '@cli-constants/snapshots.js'
import { isInteractiveSession } from '@cli-utils/terminal.js'
import { validateAuthoredGeneratedSchema } from '@core-engine/schema/add-field/validate-authored-generated-schema.js'
import { writeAuthoredGeneratedSchema } from '@core-engine/schema/add-field/write-authored-generated-schema.js'
import { snapshotRootExists } from '@core-engine/snapshots/store/snapshot-root-exists.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'
import { resolveConfigOrExit } from '@next-adapter/config/resolve-config-or-exit.js'
import { runGenerateCommand } from '../generate/run-generate-command.js'
import { cancel } from '../schema-prompts/cancel.js'
import { fail } from '../schema-prompts/fail.js'
import { createEntityOrSingleSchema } from './create-entity-or-single-schema.js'
import { createFormSchema } from './create-form-schema.js'
import { normalizeKind } from './normalize-kind.js'
import { printPreview } from './print-preview.js'
import { promptAdditionalCreateField } from './prompt-additional-create-field.js'
import { promptCreateSchemaAction } from './prompt-create-schema-action.js'
import { promptFormSubmissionColumns } from './prompt-form-submission-columns.js'
import { promptMetadata } from './prompt-metadata.js'
import { schemaFilePath } from './schema-file-path.js'

export async function runCreateCommand(
  kindInput: CreateSchemaKind,
  schemaName: string | undefined,
  options: CreateCommandOptions
): Promise<void> {
  const kind = normalizeKind(kindInput)
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()

  if (!snapshotRootExists(cwd)) {
    fail(UNSUPPORTED_PROJECT_STATE_MESSAGE)
  }

  if (!isInteractiveSession()) {
    fail('Interactive create requires a TTY.')
  }

  const config = await resolveConfigOrExit(cwd)
  const paths = resolveProjectPaths(config)
  const schemasDir = path.join(cwd, ...paths.schemasDir.split('/'))
  const metadata = await promptMetadata(kind, schemaName, schemasDir)

  const schema =
    kind === 'form'
      ? await createFormSchema(metadata, schemasDir)
      : await createEntityOrSingleSchema(kind, metadata, schemasDir)
  const filePath = schemaFilePath(kind, schemasDir, metadata.name)
  const loaded: LoadedAuthoredGeneratedSchema = {
    kind,
    name: metadata.name,
    filePath,
    schema
  }

  while (true) {
    if (kind === 'form') {
      await promptFormSubmissionColumns(schema as FormSchema)
    }

    const validationErrors = validateAuthoredGeneratedSchema(loaded)
    if (validationErrors.length > 0) {
      fail(
        `Schema validation failed:\n${validationErrors.map((error) => `  - ${error}`).join('\n')}`
      )
    }

    printPreview(loaded, cwd)
    const action = await promptCreateSchemaAction()
    if (action === 'write') {
      break
    }
    if (action === 'cancel') {
      cancel(CREATE_PROMPT_CONTEXT)
    }
    await promptAdditionalCreateField(kind, schemasDir, schema)
  }

  fs.mkdirSync(path.dirname(filePath), { recursive: true })
  writeAuthoredGeneratedSchema(loaded)
  p.log.success(`Created ${path.relative(cwd, filePath)}`)

  try {
    await runGenerateCommand(metadata.name, {
      force: false,
      acceptGenerated: Boolean(options.acceptGenerated),
      all: false,
      interactive: true,
      skipMigration: Boolean(options.skipMigration),
      cwd
    })
  } catch (error) {
    p.log.error(error instanceof Error ? error.message : String(error))
    p.log.info(
      `Schema JSON was kept. Re-run \`betterstart generate ${metadata.name}${options.skipMigration ? ' --skip-migration' : ''}\` after resolving the issue.`
    )
    process.exit(1)
  }
}
